import { useEffect, useState } from "react";
import type { AuthenticatedUser } from "@ranfac/shared";
import {
  approveWorkpaper,
  fetchMe,
  fetchWorkpaper,
  reopenWorkpaper,
  submitWorkpaper,
  type WorkpaperState,
} from "../api.js";

export function WorkpaperActions({
  reconId,
  period,
}: {
  reconId: string;
  period: string;
}) {
  const [wp, setWp] = useState<WorkpaperState | null>(null);
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([fetchWorkpaper(reconId, period), fetchMe()])
      .then(([w, u]) => {
        setWp(w);
        setUser(u);
      })
      .catch((e) => setErr(String(e)));
  }, [reconId, period]);

  async function run(action: "submit" | "approve" | "reopen") {
    setBusy(true);
    setErr(null);
    try {
      const next =
        action === "submit"
          ? await submitWorkpaper(reconId, period)
          : action === "approve"
            ? await approveWorkpaper(reconId, period)
            : await reopenWorkpaper(reconId, period);
      setWp(next);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  if (!wp || !user) return null;

  const isPreparer = user.roles.includes("Ranfac-Close-Preparer");
  const isReviewer = user.roles.includes("Ranfac-Close-Reviewer");

  const canSubmit = isPreparer && (wp.status === "draft" || wp.status === "in_review");
  const canApprove = isReviewer && wp.status === "ready_for_review";
  const canReopen =
    (isReviewer || user.roles.includes("Ranfac-Close-Approver")) &&
    (wp.status === "ready_for_review" || wp.status === "signed");

  return (
    <div className="card workpaper-actions">
      <div className="card-head" style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Review status</h2>
        <span className={`badge ${wp.status}`}>
          {wp.status.replace(/_/g, " ")}
        </span>
      </div>

      <div className="audit-line">
        {wp.preparerEmail ? (
          <>
            Prepared by <strong>{short(wp.preparerEmail)}</strong>
          </>
        ) : (
          <span className="subtle">Not yet submitted by preparer.</span>
        )}
      </div>
      <div className="audit-line">
        {wp.reviewerEmail ? (
          <>
            Reviewed by <strong>{short(wp.reviewerEmail)}</strong> on{" "}
            {relative(wp.updatedAt)}
          </>
        ) : (
          <span className="subtle">Not yet reviewed.</span>
        )}
      </div>

      {err && <div className="banner warn" style={{ marginTop: 12 }}>{err}</div>}

      <div className="toolbar" style={{ marginTop: 14, marginBottom: 0 }}>
        {canSubmit && (
          <button onClick={() => run("submit")} disabled={busy}>
            Submit for review
          </button>
        )}
        {canApprove && (
          <button onClick={() => run("approve")} disabled={busy}>
            Approve
          </button>
        )}
        {canReopen && (
          <button
            className="secondary"
            onClick={() => run("reopen")}
            disabled={busy}
          >
            Send back to preparer
          </button>
        )}
        {!canSubmit && !canApprove && !canReopen && (
          <span className="subtle">
            {wp.status === "signed"
              ? "Signed — no further action required."
              : wp.status === "locked"
                ? "Period locked."
                : "Waiting on the next reviewer in the chain."}
          </span>
        )}
      </div>
    </div>
  );
}

function short(email: string | null): string {
  if (!email) return "—";
  return email.split("@")[0] ?? email;
}

function relative(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso.replace(" ", "T") + "Z");
  const delta = (Date.now() - d.getTime()) / 1000;
  if (delta < 60) return "just now";
  if (delta < 3600) return `${Math.floor(delta / 60)}m ago`;
  if (delta < 86400) return `${Math.floor(delta / 3600)}h ago`;
  return d.toLocaleString();
}
